/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '#utils/UNSAFE_classNames';
import { useUser } from '#hooks/UNSAFE_useUser';
import { ColorProps } from '#utils/UNSAFE_interpolations/colors';
import { styles } from './themes/MeterBarStyles.css';
import { VisThresholdsColors } from '#utils/UNSAFE_meterUtils';
import { getVisThresholdColor } from '#utils/UNSAFE_meterUtils';
import { CSSProperties } from 'preact/compat';
import {
  getReferenceLineLabelTranslationOffset,
  getReferenceLinePosition,
  getReferenceLineTranslation
} from './meterUtils';

type ReferenceLineProps = {
  orientation: 'horizontal' | 'vertical';
  percent: number;
  position?: 'start' | 'end';
  color?: ColorProps['color'] | VisThresholdsColors;
  length?: string;
  label?: string;
  maxLabelWidth: number;
  maxLabelHeight: number;
  trackSize: string;
  labelStyle?: Partial<CSSStyleDeclaration>;
};

export function ReferenceLineBar({
  orientation,
  percent,
  color,
  position,
  length,
  label,
  maxLabelWidth,
  maxLabelHeight,
  trackSize,
  labelStyle
}: ReferenceLineProps) {
  const isHoriz = orientation === 'horizontal';
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const isStartPos = position === 'start';
  const percentage = `${percent}%`;
  const width = isHoriz ? undefined : length;
  const height = isHoriz ? length : undefined;
  const isLeft = (percent < 50 && !isRtl) || (percent > 50 && isRtl);
  const isBottom = percent < 50;
  return (
    <>
      <div
        class={classNames([
          styles.referenceLinesBaseStyle,
          isHoriz ? styles.referenceLinesHorizontalStyle : styles.referenceLinesVerticalStyle,
          isStartPos
            ? isHoriz
              ? styles.referenceLinesHoriontalStartStyle
              : isRtl
              ? styles.referenceLinesVerticalStartRtlStyle
              : styles.referenceLinesVerticalStartLtrStyle
            : ''
        ])}
        style={{
          left: isHoriz && !isRtl ? percentage : undefined,
          right: isHoriz && isRtl ? percentage : undefined,
          bottom: isHoriz ? undefined : percentage,
          backgroundColor: getVisThresholdColor(color),
          width,
          height,
          translate: getReferenceLineTranslation(isHoriz, isRtl, percent)
        }}></div>

      {label && (
        <div
          className={styles.labelDivStyle}
          style={{
            ...getReferenceLinePosition(
              isHoriz,
              isLeft,
              isBottom,
              isRtl,
              percent,
              maxLabelWidth,
              maxLabelHeight
            ),
            translate: getReferenceLineLabelTranslationOffset(
              isLeft,
              isBottom,
              isHoriz,
              isRtl,
              isStartPos,
              length,
              trackSize
            ),
            ...(labelStyle as CSSProperties)
          }}>
          {label}
        </div>
      )}
    </>
  );
}
